import { ColDef, GridApi, IRowNode, GridReadyEvent, GridOptions } from "ag-grid-community";
import { action, makeObservable, observable } from "mobx";
import ProgressIndicatorModel, {
  ProgressIndicatorModel as IProgressIndicatorModel
} from "../../../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";
import { EntityTypes, GridTypes } from "../../../../../../enums";
import { SingleFormModel } from "../../../../../../pages/change/forms/singleFormModel/SingleForm_model";
import { IGridToastService } from "../../../../../../services/local/gridToastService/IGridToastService";
import GridToastService from "../../../../../../services/local/gridToastService/GridToastService";
import { getEntityNameMicroFormFields } from "../../../../../../pages/change/forms/microForm/getEntityNameMicroFormFields";
import PlacesApi, { PlacesApi as IPlacesApi } from "../../../../../../services/api/v2/places/Places.api";
import { IGridUiAction } from "../base/AppGridToolbarActions_view";
import { GridModalBuilder } from "../../modals/GridModal_builder";
import { FilterStoreAddon } from "../base/addons/FilterAddon/FilterStoreAddon";
import { ColumnStateAddon } from "../base/addons/ColumnStateAddon";
import { TextWrapperAddon } from "../base/addons/TextWrapAddon";
import { AppGridToolbarType } from "../../types/AppGrid_types";
import ToasterService, { IToasterService } from "../../../../../toaster/ToasterService";
import { CommonColDefFieldNamesEnum, PlacesColDefFieldNamesEnum } from "../../../enums/AgGridColDefFieldNameEnum";
import { AppGridState } from "../base/AppGrid_view";
import I18n from "../../../../../localization/I18n";
import { TOASTER_TOAST_TIME } from "../../../../../toaster/Toaster_model";
import { PlacesGridColumnBuilder } from "./PlacesGridView_columns";
import { SHOW_CONFIRM_CREATION_MODAL, SHOW_PLACE_DELETE_CONFIRM_MODAL } from "./PlacesGridView_modals";
import { GetPlacesGridActions } from "./PlacesGridView_actions";
import { GoogleFieldModel } from "../../../../../forms/controls/googleInput/GoogleInput_model";

export class PlacesGridModel {
  organisationId: number;
  placeGroupId: number;
  placeIds: number[];
  @observable isLoading: boolean = true;
  placesGridColumnBuilder: PlacesGridColumnBuilder;
  @observable selectedItems: number[] = [];
  @observable.ref gridActions: IGridUiAction[] = [];
  httpProgress: IProgressIndicatorModel;
  placesProvider: IPlacesApi;
  refreshDataHandler: any;
  masterRefreshDataHandler: any;
  gridModalBuilder: GridModalBuilder;
  @observable filterStoreAddon: FilterStoreAddon;
  @observable columnStateAddon: ColumnStateAddon;
  @observable textWrapAddon: TextWrapperAddon;
  gridApi: GridApi;
  @observable isFilterChanged: boolean;
  @observable isColumnStateChanged: boolean;
  @observable gridColumns: ColDef<any, any>[];
  onFieldUpdate: () => void;
  gridToolbarType: AppGridToolbarType;
  @observable preSelectedItemIds: number[];
  gridType: EntityTypes | GridTypes;
  urlSearchParams: URLSearchParams;
  microPlaceForm: SingleFormModel;
  toasterService: IToasterService;
  gridToastService: IGridToastService;
  @observable gridOptions: GridOptions;

  constructor(
    organisationId: number,
    placeGroupId: number,
    refreshDataHandler: any,
    onFieldUpdate: () => void,
    gridToolbarType: AppGridToolbarType,
    preSelectedItemIds: number[],
    gridType: EntityTypes | GridTypes,
    urlSearchParams: URLSearchParams,
    placeIds: number[],

    masterRefreshDataHandler?: any
  ) {
    makeObservable(this);
    this.organisationId = organisationId;
    this.placeGroupId = placeGroupId;
    this.placeIds = placeIds;
    this.httpProgress = ProgressIndicatorModel;
    this.placesProvider = PlacesApi;
    this.refreshDataHandler = refreshDataHandler;
    this.masterRefreshDataHandler = masterRefreshDataHandler;
    this.onFieldUpdate = onFieldUpdate;
    this.toasterService = ToasterService;
    this.gridToastService = GridToastService;
    this.gridModalBuilder = new GridModalBuilder();
    this.gridToolbarType = gridToolbarType;
    this.preSelectedItemIds = preSelectedItemIds;
    this.selectedItems = preSelectedItemIds;
    this.gridType = gridType;
    this.urlSearchParams = urlSearchParams;
    this.setMicroAddForm();
    this.generateAddons();
  }

  @action
  onMount = () => {
    this.generateFn();
    this.setIsLoading(false);
  };

  @action
  generateFn = () => {
    this.generateGridConfig();
    this.generateActions();
  };

  onUnmount = () => {};

  @action
  generateGridConfig = () => {
    this.placesGridColumnBuilder = new PlacesGridColumnBuilder({
      canEdit: this.gridToolbarType !== "link-modal",
      organisationId: this.organisationId,
      onFieldUpdate: this.onFieldUpdate,
      columns: [
        CommonColDefFieldNamesEnum.Selected,
        CommonColDefFieldNamesEnum.Name,
        PlacesColDefFieldNamesEnum.StreetAddress,
        PlacesColDefFieldNamesEnum.City,
        PlacesColDefFieldNamesEnum.Country,
        PlacesColDefFieldNamesEnum.Latitude,
        PlacesColDefFieldNamesEnum.Longitude,
        CommonColDefFieldNamesEnum.CreatedBy,
        CommonColDefFieldNamesEnum.CreatedAt,
        CommonColDefFieldNamesEnum.ModifiedBy,
        CommonColDefFieldNamesEnum.UpdatedAt
      ]
    });
    this.gridColumns = this.placesGridColumnBuilder.generateColumnDefs();
  };

  generateActions = () => {
    this.setGridActions(GetPlacesGridActions(this));
  };

  @action
  setSelectedNodes = (gridApi: GridApi) => {
    const nodesToSelect: IRowNode[] = [];
    gridApi.forEachNode(node => {
      if (!!this.selectedItems?.includes(node.data.id)) {
        nodesToSelect.push(node);
      }
    });
    gridApi.setNodesSelected({ nodes: nodesToSelect, newValue: true });
  };

  @action
  generateAddons = () => {
    this.textWrapAddon = new TextWrapperAddon(null, this.gridType ?? EntityTypes.PLACES);
    this.filterStoreAddon = new FilterStoreAddon({
      projectId: null,
      gridType: this.gridType ?? EntityTypes.PLACES,
      filterHasChangedFn: this.setIsFilterChanged,
      urlSearchParams: this.urlSearchParams
    });
    this.columnStateAddon = new ColumnStateAddon({
      projectId: null,
      gridType: this.gridType ?? EntityTypes.PLACES,
      columnOrderHasChangedFn: this.setIsColumnStateChanged
    });
  };

  @action
  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  @action
  setGridActions = (gridActions: any) => {
    this.gridActions = gridActions;
  };

  @action
  setGridOptions = (gridOptions: GridOptions) => {
    this.gridOptions = gridOptions;
  };

  @action
  onGridStateUpdate = (gridState: AppGridState) => {
    this.selectedItems = gridState.selectedItems || [];
  };

  @action
  setIsColumnStateChanged = (isColumnStateChanged: boolean) => {
    this.isColumnStateChanged = isColumnStateChanged;
    this.generateActions();
  };

  @action
  setIsFilterChanged = (isFilterChanged: boolean) => {
    this.isFilterChanged = isFilterChanged;
    this.generateActions();
  };

  @action
  onFirstDataRendered = (gridApi: GridApi) => {
    const nodesToSelect: IRowNode[] = [];
    gridApi.forEachNode(node => {
      if (!!this.preSelectedItemIds?.includes(node.data.id)) {
        nodesToSelect.push(node);
      }
    });
    gridApi.setNodesSelected({ nodes: nodesToSelect, newValue: true });
  };

  @action
  onGridReady = (gridReadyEvent: GridReadyEvent) => {
    this.gridApi = gridReadyEvent.api;
  };

  @action
  setMicroAddForm = () => {
    this.microPlaceForm = new SingleFormModel();
    this.microPlaceForm.formFields = getEntityNameMicroFormFields(
      this.createMicroPlace,
      I18n.t("placeholders.myNewName", { entity: I18n.t("entities.places") }),
      true
    );
  };

  createMicroPlace = async () => {
    if (this.microPlaceForm.isSaving) return;

    const googleField = this.microPlaceForm.formFields.find(f => f instanceof GoogleFieldModel) as GoogleFieldModel;

    const place = googleField?.placeData;

    if (!place || !place.geometry) {
      this.toasterService.showErrorToast();
      return;
    }

    const getAddressComponent = (type: string) =>
      place.address_components?.find(c => c.types.includes(type))?.long_name ?? "";

    const newPlace: FP.Entities.IPlaceDetail = {
      name: place.name || place.formatted_address || "Unnamed Place",
      streetAddress: getAddressComponent("route"),
      city: getAddressComponent("locality"),
      country: getAddressComponent("country"),
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
      id: Number(place.place_id),
      placeGroupId: this.placeGroupId
    };

    this.httpProgress.showOverlay();
    const result = await this.placesProvider.createPlace(this.organisationId, newPlace);
    this.httpProgress.hideOverlay();
    if (result?.response?.data?.isError) {
      this.toasterService.showErrorToast().setContent(result.response.data.message);
      return;
    }

    await this.refreshDataHandler();
    this.microPlaceForm.resetFields();
    this.toasterService
      .showSuccessToast()
      .setContent(<span>{I18n.t("phrases.itemCreatedSuccessfully", { item: I18n.t("entities.places") })}</span>)
      .startTimer(TOASTER_TOAST_TIME.NORMAL);
  };

  confirmCreatePlace = async (name: string): Promise<boolean> => {
    return SHOW_CONFIRM_CREATION_MODAL(name);
  };

  showPlaceConfirmDeleteModal = () => {
    return SHOW_PLACE_DELETE_CONFIRM_MODAL(this.selectedItems, this.removeItems);
  };

  removeItems = async (itemIds: number[]) => {
    this.httpProgress.showOverlay();
    let res = await this.placesProvider.deletePlace(this.organisationId, itemIds);
    if (res && !res.isError) {
      await this.refreshDataHandler();
    }
    this.httpProgress.hideOverlay();

    return res.payload;
  };
}
